/**
 * Reading an amount the way a person typed it.
 *
 * People type money in whatever shape their phone keyboard and their habits
 * produce: `1545`, `1 545`, `1,545`, `1,545.00`, `95,50`. The old parser kept
 * the first separator and dropped the rest, so `1,545` came out as ₾1.55 and
 * `1,545.00` as nothing at all — a line that quietly vanished from the total.
 *
 * The rule here: whichever separator comes last is the decimal point, unless
 * exactly three digits follow it, in which case it was grouping thousands.
 * Two digits after a separator is always money; three is always a thousand.
 */
export function parseMoneyToTetri(text: string): number {
  const cleaned = text.replace(/[^\d.,]/g, '');
  if (!cleaned) return 0;

  const lastSeparator = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));

  let normalised: string;
  if (lastSeparator === -1) {
    normalised = cleaned;
  } else {
    const after = cleaned.slice(lastSeparator + 1);
    const before = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');

    // Three digits behind it and a real number in front: that was a thousand.
    // "0.005" is excluded on purpose — nobody writes nought thousand.
    const thousands = after.length === 3 && before.length > 0 && Number(before) > 0;
    normalised = thousands ? `${before}${after}` : `${before || '0'}.${after}`;
  }

  const value = Number(normalised);
  if (!Number.isFinite(value) || value < 0) return 0;

  return Math.round(value * 100);
}

/** The same amount back as text, for a field that was filled from stored data. */
export const tetriToInput = (tetri: number): string =>
  (tetri % 100 === 0 ? String(tetri / 100) : (tetri / 100).toFixed(2));
